import React, { useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/store/store";
import { LEAD_DROP_POPUP_SECTION_VARIANTS } from "../../../constants";
import { handleCloseLeaddrop } from "@/store/slices/leaddrop";
import { trackCrossClick, trackPopupOpen } from "@/customAnalytics/rudderStackEvents";
import { IUniversitySettings } from "@/api/admin/universitySettings";
import { ILeadDropPopup } from "@/api/admin/leadDropPopup";
import { ICourse } from "@/api/admin/course";
import { ILeaddropFormData } from "@/store/slices/leaddrop";
import { UNIVERSITY_ID } from "@/constants/admin";
import ConfigLeadFormPopup from "@/components/organisms/leadForm/ConfigLeadFormPopup";
import { useResolvedLeadForm } from "@/components/organisms/leadForm/useResolvedLeadForm";

interface DefaultValues {
  phone?: string;
  name?: string;
  email?: string;
  qualification?: string;
  interest?: string;
  course?: string;
  media?: string;
  campaign?: string;
  termsAccepted?: boolean;
}

interface LeaddropComponentProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  trigger?: React.ReactNode;
  onClose?: () => void;
  data?:
    | {
        formData?: ILeadDropPopup | null;
        courses?: ICourse[];
        defaultValues?: DefaultValues;
      }
    | ILeadDropPopup
    | null;
  variant?: string;
  mobileData?: unknown;
  isMobile?: boolean;
  enrollmentBoundCourseSelectValue?: string | null;
  lockEnrollmentCourse?: boolean;
}

const OpenLeaddrop = ({
  leaddropPopup,
}: {
  leaddropPopup: IUniversitySettings | null;
}) => {
  const dispatch = useDispatch();

  const leaddropState = useSelector((state: RootState) => state.leaddrop);

  const { formData, courses, defaultValues, mobileData, isMobile } =
    leaddropState;

  const {
    isOpen,
    variant,
    downloadLink,
    isExitFormOpen,
    isDelayFormOpen,
    lockEnrollmentCourse,
    enrollmentBoundCourseSelectValue,
  } = leaddropState;

  /**
   * Lead popup 8 only: when the opener didn't set an explicit course binding in
   * the slice, fall back to the page's linked course (page.course_id →
   * globalslice, same derivation as LeaddropForm8). This makes the verified
   * user × conflicting-course re-registration flow reachable from the popup;
   * other variants keep their slice-only binding.
   */
  const pageCourse = useSelector(
    (state: RootState) => state.globalslice.course,
  ) as ICourse | undefined;
  // "8" = LEAD_DROP_POPUP_VARIANTS.LEAD_DROP_POPUP_8 (sectionTypes).
  const isLeadPopup8 = String(leaddropPopup?.variant ?? "") === "8";
  const pageCourseKey = useMemo(() => {
    const erp = String(pageCourse?.erp_unicode ?? "").trim();
    if (erp) return erp;
    const id = pageCourse?.id != null ? String(pageCourse.id).trim() : "";
    return id || null;
  }, [pageCourse]);
  const effectiveLockEnrollmentCourse =
    lockEnrollmentCourse || (isLeadPopup8 && Boolean(pageCourseKey));
  const effectiveBoundCourseSelectValue = lockEnrollmentCourse
    ? enrollmentBoundCourseSelectValue
    : isLeadPopup8
      ? pageCourseKey
      : enrollmentBoundCourseSelectValue;

  /**
   * A CMS-authored popup form for this page, if one is configured. Surface-gated, so a
   * config authored as an inline form never hijacks the popup.
   */
  const customPopup = useResolvedLeadForm("popup");

  const LeaddropComponent = useMemo(() => {
    if (!leaddropPopup?.section || !leaddropPopup?.variant) {
      return null;
    }

    return LEAD_DROP_POPUP_SECTION_VARIANTS[leaddropPopup.variant] as
      | React.ComponentType<LeaddropComponentProps>
      | undefined;
  }, [leaddropPopup]);

  // ─── RUDDERSTACK (new, sheet-aligned) — remove block + delete rudderStackEvents.ts to revert ───
  // popup_open / cross_click: fire on the drawer's display transitions —
  // popup_open when it becomes displayed (isOpen && a popup variant exists),
  // cross_click when it closes. Both re-fire on each open/close cycle. The
  // popup name captured at open is reused for the close so they stay consistent.
  // NOTE: cross_click here fires on ANY close (× button, backdrop, ESC), not the
  // × button only. DOUBT: confirm popup_name / component_name scheme.
  // The custom popup counts as displayed exactly like a legacy variant — otherwise the
  // whole funnel silently loses popup_open/cross_click the moment a university switches.
  const isPopupDisplayed = isOpen && Boolean(customPopup || LeaddropComponent);
  const popupName = isExitFormOpen
    ? "exit_intent"
    : isDelayFormOpen
      ? "timer"
      : String(variant || "lead_popup");
  const popupWasDisplayed = useRef(false);
  const openPopupName = useRef("");
  useEffect(() => {
    if (isPopupDisplayed && !popupWasDisplayed.current) {
      openPopupName.current = popupName;
      trackPopupOpen({ popup_name: popupName });
    } else if (!isPopupDisplayed && popupWasDisplayed.current) {
      trackCrossClick({ component_name: openPopupName.current || popupName });
    }
    popupWasDisplayed.current = isPopupDisplayed;
  }, [isPopupDisplayed, popupName]);
  // ─── /RUDDERSTACK (new) ───

  /**
   * The custom form wins when one is configured for this surface. Checked BEFORE the
   * legacy guard on purpose: a university may have a custom popup and no legacy popup
   * variant at all, and bailing out on the missing variant would render nothing.
   */
  if (customPopup) {
    return (
      <ConfigLeadFormPopup
        config={customPopup}
        universityId={UNIVERSITY_ID}
        open={isOpen}
        onOpenChange={(next: boolean) => {
          if (!next) dispatch(handleCloseLeaddrop());
        }}
        onClose={() => dispatch(handleCloseLeaddrop())}
        context={defaultValues as Record<string, unknown> | undefined}
        // How the popup was OPENED, carried through unchanged. A brochure CTA sets the
        // download variant on the slice; without these two the custom form would thank
        // the visitor for registering and never hand over the file, which is the same
        // silent failure a missing `variant` causes for the legacy popups.
        variant={variant}
        downloadLink={downloadLink}
      />
    );
  }

  if (!LeaddropComponent) {
    return null;
  }

  return (
    LeaddropComponent && (
      <LeaddropComponent
        mobileData={mobileData}
        isMobile={isMobile}
        open={isOpen}
        data={{
          formData: formData as ILeadDropPopup | null,
          courses: courses,
          defaultValues: defaultValues,
        }}
        onOpenChange={(open: boolean) => {
          if (!open) {
            dispatch(handleCloseLeaddrop());
          }
        }}
        onClose={() => dispatch(handleCloseLeaddrop())}
        variant={variant}
        lockEnrollmentCourse={effectiveLockEnrollmentCourse}
        enrollmentBoundCourseSelectValue={effectiveBoundCourseSelectValue}
      />
    )
  );
};

export default OpenLeaddrop;
